// filepath: /Users/avinash/Drop/df-website/src/app/services/environment.service.ts
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FacilityConfig, Link, PromoBannerStrip } from 'src/environments/environment.interface';
import { ConfigService } from './config.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root', // Makes the service available application-wide
})
export class EnvironmentService {
  private _facilityConfig: FacilityConfig | null = null;

  constructor(
    private configService: ConfigService,
    private localStorageService: LocalStorageService
  ) {}

  get facilityConfig(): FacilityConfig {
    if (!this._facilityConfig) {
      this._facilityConfig = this.configService.getConfig();
    }
    return this._facilityConfig;
  }

  /**
   * Get the entire FacilityConfig object.
   */
  public getFacilityConfig(): FacilityConfig {
    return this.facilityConfig;
  }

  /**
   * Minimum age for signup / date-of-birth max (from facility config).
   * Defaults: Drop Fitness 15, all other submerchants 13 (matches mobile when config key is absent).
   */
  public getMinimumAge(): number {
    const config = this.getFacilityConfig();
    const fromConfig = config.minimumAge;
    if (typeof fromConfig === 'number' && fromConfig > 0 && Number.isFinite(fromConfig)) {
      return Math.floor(fromConfig);
    }
    return config.submerchant === 'dropfitness' ? 15 : 13;
  }

  public getPromoBanner(page: string): PromoBannerStrip {
    const promoBanner = this.getFacilityConfig().promoBannerStrip?.[page];
    return promoBanner ? promoBanner : {
      show: false,
      text: '',
      link: '',
      linkText: '',
      backgroundColor: '',
      textColor: '',
      showCloseButton: true,
    };
  }

  /**
   * Get a specific link url by its ID.
   * @param id The ID of the link to retrieve.
   */
  public getLinkUrlById(id: string): string {
    const link = this.facilityConfig.links.find((link) => link.id === id);
    return link?.url ? link.url : '';
  }

  /**
   * Returns the logo (website) link URL. If a website URL is set in config, use it;
   * otherwise keep the user on the current origin ('/').
   */
  public getLogoLinkUrl(): string {
    const url = (this.getLinkUrlById('website') || '').trim();
    if (!url) return '/';
    return url;
  }

  /**
   * Get a specific link by its ID.
   * @param id The ID of the link to retrieve.
   */
  public getLinkById(id: string): Link {
    const link = this.facilityConfig.links.find((link) => link.id === id);
    return link ? link : { id: '', name: '', url: '', target: '_self', category: '', icon: '' }; // Default to an empty link object if not found
  }

  /**
   * Get all links by category.
   * @param category The category of links to retrieve.
   */
  public getLinksByCategory(category: string): Link[] {
    return this.facilityConfig.links
      .filter((link) => link.category?.toLowerCase() === category.toLowerCase());
  }

  /**
   * Get a specific property from FacilityConfig.
   * @param key The key of the property to retrieve.
   */
  public getProperty<T extends keyof FacilityConfig>(key: T): FacilityConfig[T] {
    return this.facilityConfig[key];
  }

  getStripeAccountId(): string {
    return this.getStripeAccountIdForLocation(this.localStorageService.getLocation()?.Id);
  }

  /**
   * Get Stripe Connect account ID for a specific location.
   * Use this when the Stripe account must match the backend (e.g. SetupIntent created for a member's location).
   */
  getStripeAccountIdForLocation(locationId: number | undefined): string {
    const stripeAccounts = this.getFacilityConfig()?.stripe?.accountId?.split(',') || [];

    if (stripeAccounts.length === 0) {
      return '';
    }

    let index = 0;
    if (locationId != null && locationId > 0 && locationId <= stripeAccounts.length) {
      index = locationId - 1;
    }
    return stripeAccounts[index]?.trim() || stripeAccounts[0]?.trim() || '';
  }
}